/**
 * Shared text-size toolbar (zoom in/out only).
 *
 * Smaller sibling of Reading's `TextToolbar` — reuses the same zoom
 * in/out icons and the same 'small' | 'medium' | 'large' semantics but
 * omits the highlighter (which only applies to Reading). Used by the
 * Writing prompt panel to optionally expose a font-size control.
 */

import { Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export type TextSize = 'small' | 'medium' | 'large';

export interface TextSizeToolbarProps {
  readonly textSize: TextSize;
  readonly onTextSizeChange: (size: TextSize) => void;
  /** Optional decrease-tooltip copy; defaults to "Decrease text size". */
  readonly decreaseLabel?: string;
  /** Optional increase-tooltip copy; defaults to "Increase text size". */
  readonly increaseLabel?: string;
  /** Extra class appended to the root element. */
  readonly className?: string;
}

export function TextSizeToolbar({
  textSize,
  onTextSizeChange,
  decreaseLabel = 'Decrease text size',
  increaseLabel = 'Increase text size',
  className,
}: TextSizeToolbarProps) {
  const handleSizeDecrease = () => {
    if (textSize === 'large') onTextSizeChange('medium');
    else if (textSize === 'medium') onTextSizeChange('small');
  };

  const handleSizeIncrease = () => {
    if (textSize === 'small') onTextSizeChange('medium');
    else if (textSize === 'medium') onTextSizeChange('large');
  };

  return (
    <div
      className={cn(
        'flex items-center gap-0.5 sm:gap-1 bg-card/90 sm:bg-card/95 border border-border rounded-lg p-1 sm:p-1.5',
        className,
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 rounded-md"
            onClick={handleSizeDecrease}
            disabled={textSize === 'small'}
          >
            <Minus className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{decreaseLabel}</p>
        </TooltipContent>
      </Tooltip>

      <span className="font-ui text-xs font-medium text-muted-foreground px-1 min-w-[24px] text-center">
        A
        <span
          className={cn(
            textSize === 'small' && 'text-[10px]',
            textSize === 'medium' && 'text-xs',
            textSize === 'large' && 'text-sm',
          )}
        >
          {(() => {
            if (textSize === 'small') return '-';
            if (textSize === 'large') return '+';
            return '';
          })()}
        </span>
      </span>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 rounded-md"
            onClick={handleSizeIncrease}
            disabled={textSize === 'large'}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{increaseLabel}</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
